<template>
    <div class="bg-white account-details-page">
        <div class="p-5 bg-red-50 font-medium text-center border-gray-200 border-b" v-for="message in messageRules">
            <div v-html="message"></div>
        </div>
        <div v-if="!messageRules">
            <div class="p-5 bg-red-50 font-medium text-center border-gray-200 border-b">
                <div class="mx-auto md:w-2/3 lg:w-1/2 xl:w-1/3 2xl:w-1/4">
                    Please call <a :href="`tel:${$root.phoneNumberForLocation('account-details-messages-rules-header')}`">{{ $root.phoneNumberForLocation('account-details-messages-rules-header') }}</a> to speak with a representative at or e-mail <a :href="`mailto:${data.consumerPortalEmail}`">{{ data.consumerPortalEmail }}</a> for more information.
                </div>
            </div>
        </div>

        <div v-if="(declinedStatus || pifStatus || sifStatus)"
            class="p-5 bg-red-50 font-medium text-center border-gray-200 border-b">

            <div class="mx-auto md:w-2/3 lg:w-1/2 xl:w-1/3 2xl:w-1/4">
                <template v-if="declinedStatus !== false">
                    Your account is currently not in good standing.
                    <template v-if="declined">
                        Your most recent payment of <strong>{{ declined.amount | money }}</strong> has declined.
                    </template>
                </template>
                <template v-else-if="pifStatus !== false">
                    Congratulations, your account has been paid in full!
                </template>
                <template v-else-if="sifStatus !== false">
                    Congratulations, your account has been settled in full!
                </template>
            </div>

        </div>
        <div class="bg-gray-100">

            <div class="p-5 mx-auto md:w-2/3 lg:w-1/2 xl:w-1/3 2xl:w-1/4">
                <div class="flex items-center mb-4">
                    <div>
                        <svg :class="`inline-block h-10 w-10 text-${$root.theme}-500`" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clip-rule="evenodd" />
                        </svg>
                    </div>
                    <div class="ml-3">
                        <p class="font-semibold pt-1 text-lg">
                            Hello, {{ `${account?.first_name} ${account?.last_name}` }}!
                        </p>
                    </div>
                </div>

                <a v-if="! closed" @click.prevent="$root.$refs.app.showingModal.updateContactInformation = true" class="inline-block mb-4 text-center w-full text-blue-600 py-3 px-4 border border-blue-600 shadow-sm leading-4 shadow rounded-md focus:outline-none hover:bg-blue-100 cursor-pointer text-sm">
                    <span class="font-medium underline">Update Contact Information</span>
                </a>

                <div>
                    <p class="text-sm font-medium">Our Reference Number:</p>
                    <p v-if="account" class="font-bold">{{ account.id }}</p>

                    <a v-if="multiAccounts && multiAccounts.length > 0"
                        @click="$root.$emit('select-account', multiAccounts)"
                        class="mt-3 w-full text-white items-center inline-flex items-center py-3 px-4 border border-red-600 border-transparent shadow-sm leading-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 bg-red-500 hover:bg-red-600 focus:ring-red-300 cursor-pointer">
                        <div class="text-lg mr-4">
                            <i class="fa fa-info-circle fa-fw"></i>
                        </div>
                        <div class="text-sm text-left">
                            You have multiple accounts with our office. <strong class="whitespace-nowrap">Click to view.</strong>
                        </div>
                    </a>
                </div>

                <div class="border-gray-200 border-t pt-3 mt-2">
                    <p class="text-sm font-medium">Current Creditor Account Number:</p>
                    <p class="font-bold">{{ account.client_reference_num }}</p>
                </div>

                <div class="border-gray-200 border-t pt-3 mt-2">
                    <p class="text-sm font-medium">Current Creditor To Whom Debt Is Owed:</p>
                    <p class="font-bold">{{ account.current_creditor }}</p>
                </div>

                <div class="border-gray-200 border-t pt-3 mt-2">
                    <p class="text-sm font-medium">Original Creditor:</p>
                    <p class="font-bold">{{ account.lender }}</p>
                </div>

                <div v-if="account.original_creditor_number_redacted_email" class="border-gray-200 border-t pt-3 mt-2">
                    <p class="text-sm font-medium">Original Creditor Account Number:</p>
                    <p class="font-bold">{{ account.original_creditor_number_redacted_email }}</p>
                </div>

                <a v-if="canDoEarlyPayOff" :href="`${data.consumerPortalBaseUrl}/early-payoff`">
                    <button type="button" :class="`bg-${$root.theme}-300 hover:bg-${$root.theme}-400 focus:ring-${$root.theme}-200 mt-3 w-full justify-center text-black items-center inline-flex items-center px-3 pt-4 pb-3 border border-transparent shadow-sm leading-4 font-semibold rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2`">
                        <template v-if="! canDoEarlyPayoffDiscount">
                            View Fast-Track Payoff!
                        </template>
                        <template v-else>
                            View Fewer Payments, Bigger Discount!
                        </template>
                    </button>
                </a>

                <button type="button"
                    v-if="canDoExtraPayment"
                    @click="$root.$refs.app.showPaymentExtra({ account, billingProfile })"
                    :class="`bg-${$root.theme}-300 hover:bg-${$root.theme}-400 focus:ring-${$root.theme}-200 mt-3 w-full justify-center text-black items-center inline-flex items-center px-3 pt-4 pb-3 border border-transparent shadow-sm leading-4 font-semibold rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2`">
                    Make Additional Payment
                </button>
            </div>

        </div>
        <div class="mx-auto md:w-2/3 lg:w-1/2 xl:w-1/3 2xl:w-1/4">
            <div class="p-5 bg-white">

                <payments-tracker :account="account" :billing-profile="billingProfile" :declined-status="declinedStatus" />
                <download-release-letter v-if="canDownloadReleaseLetter" :account="account" :data="data" />
                <payments-timeline :account="account" :payments="payments" :billing-profile="billingProfile" :has-processed-payments-today="hasProcessedPaymentsToday" :can-reschedule-next-pending-payment="canRescheduleNextPendingPayment" />

                <a v-if="account.needs_new_payment_plan" :href="`${data.consumerPortalBaseUrl}/nsf-offers`">
                    <button type="button" :class="`mt-3 w-full justify-center text-black items-center inline-flex items-center px-3 pt-4 pb-3 border border-transparent shadow-sm leading-4 font-semibold rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 bg-${$root.theme}-300 hover:bg-${$root.theme}-400 focus:ring-${$root.theme}-200`">
                        Setup New Payment Plan
                    </button>
                </a>

            </div>
            <div class="px-5 pb-5">
                <div>
                    <p class="text-sm leading-tight font-medium text-center">
                        <template v-if="data.hasOwnProperty('consumerPortalNumber') && data.hasOwnProperty('consumerPortalEmail')">
                            In order to change your arrangements (dates and / or amounts) please contact our office at <a :href="`tel:${$root.phoneNumberForLocation('footer')}`" :class="`font-semibold break-keep inline-block text-${$root.theme}-500`">{{ $root.phoneNumberForLocation('footer') }}</a> or email us at  <a :href="`mailto:${data.consumerPortalEmail}`" :class="`font-semibold text-${$root.theme}-500`">{{ data.consumerPortalEmail }}.</a>
                        </template>
                        <template v-else-if="data.hasOwnProperty('consumerPortalNumber') && ! data.hasOwnProperty('consumerPortalEmail')">
                            In order to change your arrangements (dates and / or amounts) please contact our office at <a :href="`tel:${$root.phoneNumberForLocation('footer')}`" :class="`font-semibold break-keep inline-block text-${$root.theme}-500`">{{ $root.phoneNumberForLocation('footer') }}.</a>
                        </template>
                        <template v-else-if="data.hasOwnProperty('consumerPortalEmail') && ! data.hasOwnProperty('consumerPortalNumber')">
                            In order to change your arrangements (dates and / or amounts) please email our office at <a :href="`mailto:${data.consumerPortalEmail}`" :class="`font-semibold break-keep inline-block text-${$root.theme}-500`">{{ data.consumerPortalEmail }}.</a>
                        </template>
                    </p>
                </div>

                <a :href="`${data.consumerPortalBaseUrl}/logout`">
                    <button id="logout-button" type="button" class="mt-4 w-full justify-center text-black items-center inline-flex items-center px-3 text-lg pt-4 pb-3 border border-transparent border-gray-300 shadow-sm leading-4 font-medium rounded-md bg-gray-50 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200">
                        <svg class="-ml-0.5 mr-3 h-4 w-4 mb-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Logout
                    </button>
                </a>
            </div>
        </div>
    </div>
</template>

<script>
    import Mixins from '../mixins';
    import PaymentsTracker from './AccountDetails/Tracker';
    import PaymentsTimeline from './AccountDetails/Timeline';
    import DownloadReleaseLetter from "./AccountDetails/DownloadReleaseLetter.vue";
    import MakeExtraPayment from '../components/Modals/MakeExtraPayment.vue'

    export default {
        mixins: [Mixins],

        components: {
            MakeExtraPayment,
            PaymentsTracker,
            PaymentsTimeline,
            DownloadReleaseLetter
        },

        data() {
            return {
                showingExtraPayment: false,
            }
        },

        mounted() {
            if (this.billingProfile && this.billingProfile.is_card_expiring_within_days) {
                setTimeout(() => {
                    this.$root.$emit('show-billing-profile-expiration-warning', this.billingProfile);
                }, 500);
            }
        },

        props: {
            data: {},
            account: {},
            closed: {},
            payments: {},
            declined: null,
            pifStatus: false,
            sifStatus: false,
            multiAccounts: {},
            billingProfile: {},
            paymentRetryPath: {},
            declinedStatus: false,
            canDoExtraPayment: {
                type: Boolean,
                default: false
            },
            canDoEarlyPayOff: {
                type: Boolean,
                default: false
            },
            canDoEarlyPayoffDiscount: {
                type: Boolean,
                default: false
            },
            canRescheduleNextPendingPayment: {
                type: Boolean,
                default: false
            },
            messageRules: null,
            canDownloadReleaseLetter: false,
            hasProcessedPaymentsToday: {}
        },

        computed: {
            /**
             *  @returns {{
             *      cc_type: (string),
             *      payment_type: (string),
             *      cc_last_four: (string)
             *  }}
             */
            billing() {
                return {
                    'payment_type': this.billingProfile.payment_type,
                    'cc_last_four': this.billingProfile.account_last_4,
                    'cc_type': this.billingProfile.cc_type
                };
            }
        },

        methods: {
            changeAccount(account) {
                this.$http.post(`${this.data.consumerPortalBaseUrl}/api/account-verification`, {
                    account_id: account.id,
                    ssn_last_four: account.contact.ssn_last_four,
                    zip: account.contact.zip
                }).then(({ data }) => {
                    this.gtagEvent('login-successful');
                    this.gtagEvent('switched-account');

                    if (data.is_first_login) {
                        this.gtagEvent('first-login-attempt');
                    }

                    setTimeout(() => {
                        this.$root.$emit('loading');

                        if (! data.show_agreement) {
                            this.$nextTick(() => window.location = `${this.data.consumerPortalBaseUrl}/early-payoff`);
                        } else {
                            this.$root.$emit('agreement', {
                                agreement: data.agreement,
                                callback: () => {
                                    this.$nextTick(() => window.location = `${this.data.consumerPortalBaseUrl}/early-payoff`);
                                }
                            });
                        }
                    }, 1000);
                });
            }
        }
    }
</script>
